import { useMemo } from "react"
import { graphql, GraphQLSubscriptionConfig } from "relay-runtime"
import { useConditionalSubscription } from "@/modules/patients/hooks/useConditionalSubscription"
import { usePatientAttachmentSubscription as usePatientAttachmentSubscriptionType } from "@/modules/patients/services/__generated__/usePatientAttachmentSubscription.graphql"

export const patientAttachmentSubscription = graphql`
  subscription usePatientAttachmentSubscription($appointmentId: ID!) {
    attachmentsChanged(appointmentId: $appointmentId) {
      attachments {
        id
        url
        thumbnailUrl
        uploadDate
        attachmentType
        fileName
        uploadStatus
        uploadSource
      }
      reason
    }
  }
`

const usePatientAttachmentSubscription = (appointmentId: string | null) => {
  const config: GraphQLSubscriptionConfig<usePatientAttachmentSubscriptionType> = useMemo(
    () => ({
      subscription: patientAttachmentSubscription,
      variables: {
        appointmentId: appointmentId ?? "",
      },
      updater(store, data) {
        if (!appointmentId || !data?.attachmentsChanged) return

        const { attachments, reason } = data.attachmentsChanged
        if (!attachments || !reason) return

        const appointmentRecordProxy = store.get(appointmentId)
        if (!appointmentRecordProxy) return

        const appointmentAttachments = appointmentRecordProxy.getLinkedRecords("appointmentAttachments") || []
        const fileUploadRequest = appointmentRecordProxy.getLinkedRecord("fileUploadRequest")
        const isPendingRequest = fileUploadRequest && fileUploadRequest.getValue("status") === "PENDING"

        if (reason === "UPLOAD_SESSION_REVERTED") {
          const idsToRemove = attachments.map((a) => a.id)

          const newAttachments = appointmentAttachments.filter((a) => {
            const id = a.getValue("id")?.toString()
            return id && !idsToRemove.includes(id)
          })

          appointmentRecordProxy.setLinkedRecords(newAttachments, "appointmentAttachments")
        } else if (reason === "UPLOAD_STATUS_CHANGED") {
          attachments.forEach((attachment) => {
            if (isPendingRequest && attachment.uploadSource === "PATIENT") {
              fileUploadRequest.setValue("UPLOADED", "status")
            }

            const existingAttachment = appointmentAttachments.find((att) => att.getDataID() === attachment.id)

            if (!existingAttachment) {
              const newAttachment = store.get(attachment.id)
              if (newAttachment) appointmentAttachments.unshift(newAttachment)
            }
          })

          appointmentRecordProxy.setLinkedRecords(appointmentAttachments, "appointmentAttachments")
        }
      },
    }),
    [appointmentId],
  )

  return useConditionalSubscription<usePatientAttachmentSubscriptionType>(config, {
    skip: !appointmentId,
  })
}

export { usePatientAttachmentSubscription }
